<script setup lang="ts">
import { StepContentBody, StepAnswer, Input } from '@models/quiz';
import { capitalize } from 'lodash';
import { vMaska } from 'maska';
import { computed, onMounted, ref, watch } from 'vue';

type Answer = StepAnswer<Input>

const props = withDefaults(defineProps<{
  modelValue: Record<string, StepAnswer<Input>>;
  columns?: string;
  content: StepContentBody;
  answers: Answer[];
}>(), {
  columns: '4',
});

const emit = defineEmits<{
  'update:modelValue': [values: Record<string, StepAnswer<Input>>]
  'submit': []
}>();

const columnClass = computed(() => `sm:f-col-${props.columns} f-col-full`);

const values = ref(props.modelValue);
const inputs = ref<HTMLInputElement[]>([]);

watch(values, (values) => {
  emit('update:modelValue', values);
});

const maskOptions = {
  preProcess(value: string) {
    return value
      .replaceAll(/^\s+/gi, '')
      .replaceAll(/\s{2,}/gi, ' ')
      .split(' ')
      .map(capitalize)
      .join(' ');
  },
};

onMounted(() => {
  inputs.value[0]?.focus();
});

const focusNextInput = (event: Event) => {
  const currentInputIndex = Array.from(inputs.value).indexOf(event.target as HTMLInputElement);
  if (currentInputIndex < inputs.value.length - 1) {
    inputs.value[currentInputIndex + 1]?.focus();
  } else {
    submit();
  }
};

const submit = () => {
  emit('submit');
};
</script>

<template>
  <div class="f-row">
    <div :class="columnClass">
      {{ content.label }}

      <div class="mt-16 flex gap-16">
        <!-- TODO:
          - Update vMaska version
          - Change to BaseInput component
          - Fix type error -->
        <input
          v-for="answer in answers"
          ref="inputs"
          :key="answer.id"
          v-maska:[maskOptions]
          class="input w-full"
          type="text"
          :value="values[answer.id]?.value"
          :placeholder="answer.content[0].content.placeholder"
          data-maska="A"
          data-maska-tokens="A:[A-z'-\s]:multiple"
          data-maska-eager
          :maxlength="35"
          :data-testpl="`input-${answer.id}`"
          spellcheck="false"
          @input="(event) => {
            const value = (event.target as HTMLInputElement).value;
            if (!value) {
              delete values[answer.id];
              return;
            }
            values[answer.id] = { ...answer, value };
          }"
          @keydown.enter="focusNextInput"
        >
      </div>
    </div>
  </div>
</template>
